import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "../../lib/supabaseClient";

import "./css/HomeActivities.css";

const HomeActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivities = async () => {
      const { data, error } = await supabase
        .from("activities")
        .select(`
          id,
          title,
          slug,
          category,
          activity_date,
          location,
          description,
          cover_image,
          featured,
          published
        `)
        .eq("published", true)
        .eq("featured", true)
        .order("activity_date", {
          ascending: false,
          nullsFirst: false,
        })
        .limit(3);

      if (error) {
        console.error("Error loading homepage activities:", error);
        setLoading(false);
        return;
      }

      setActivities(data || []);
      setLoading(false);
    };

    loadActivities();
  }, []);

  /*
   * Don't render anything while loading or if there
   * are no featured activities.
   *
   * This prevents an empty section from appearing
   * on the homepage.
   */
  if (loading || activities.length === 0) {
    return null;
  }

  const featuredActivity = activities[0];
  const secondaryActivities = activities.slice(1);

  const formatDate = (date) => {
    if (!date) return "";

    const [year, month, day] = date.split("-");

    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );

    return localDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const truncateText = (text, length = 150) => {
    if (!text) return "";

    if (text.length <= length) {
      return text;
    }

    return `${text.substring(0, length).trim()}…`;
  };

  return (
    <section className="home-activities">

      <div className="home-container home-activities-container">

        {/* =================================================
            SECTION INTRO
            ================================================= */}

        <div className="home-activities-heading">

          <div className="home-section-eyebrow">
            BEYOND THE CLINIC
          </div>

          <div className="home-activities-heading-row">

            <div>
              <h2>
                Learning, sharing,
                <em>and caring.</em>
              </h2>

              <p>
                A practice engaged in education, community
                wellness and meaningful conversations beyond
                the consultation room.
              </p>
            </div>

            <Link
              to="/activities"
              className="home-activities-view-all"
            >
              View All Activities
              <span>→</span>
            </Link>

          </div>

        </div>


        {/* =================================================
            FEATURED ACTIVITY
            ================================================= */}

        <div className="home-activities-layout">

          <Link
            to={`/activities/${featuredActivity.slug}`}
            className="home-activity-featured"
          >

            <div className="home-activity-featured-image">

              {featuredActivity.cover_image ? (
                <img
                  src={featuredActivity.cover_image}
                  alt={featuredActivity.title}
                  loading="lazy"
                />
              ) : (
                <div className="home-activity-image-placeholder">
                  Sudhamshu Ayurveda
                </div>
              )}

              <div className="home-activity-image-label">
                FEATURED
              </div>

            </div>


            <div className="home-activity-featured-content">

              {featuredActivity.category && (
                <span className="home-activity-category">
                  {featuredActivity.category}
                </span>
              )}

              <h3>
                {featuredActivity.title}
              </h3>

              <div className="home-activity-meta">

                {featuredActivity.activity_date && (
                  <span>
                    {formatDate(featuredActivity.activity_date)}
                  </span>
                )}

                {featuredActivity.activity_date &&
                  featuredActivity.location && (
                    <span className="home-activity-meta-dot">
                      ·
                    </span>
                  )}

                {featuredActivity.location && (
                  <span>
                    {featuredActivity.location}
                  </span>
                )}

              </div>

              {featuredActivity.description && (
                <p>
                  {truncateText(featuredActivity.description, 190)}
                </p>
              )}

              <span className="home-activity-link">
                Explore Activity
                <span>→</span>
              </span>

            </div>

          </Link>


          {/* =================================================
              SECONDARY ACTIVITIES
              ================================================= */}

          {secondaryActivities.length > 0 && (
            <div className="home-activities-secondary">

              {secondaryActivities.map((activity) => (
                <Link
                  key={activity.id}
                  to={`/activities/${activity.slug}`}
                  className="home-activity-small"
                >

                  <div className="home-activity-small-image">

                    {activity.cover_image ? (
                      <img
                        src={activity.cover_image}
                        alt={activity.title}
                        loading="lazy"
                      />
                    ) : (
                      <div className="home-activity-image-placeholder">
                        Sudhamshu Ayurveda
                      </div>
                    )}

                  </div>


                  <div className="home-activity-small-content">

                    {activity.category && (
                      <span className="home-activity-category">
                        {activity.category}
                      </span>
                    )}

                    <h3>
                      {activity.title}
                    </h3>

                    <div className="home-activity-meta">

                      {activity.activity_date && (
                        <span>
                          {formatDate(activity.activity_date)}
                        </span>
                      )}

                      {activity.activity_date &&
                        activity.location && (
                          <span className="home-activity-meta-dot">
                            ·
                          </span>
                        )}

                      {activity.location && (
                        <span>
                          {activity.location}
                        </span>
                      )}

                    </div>

                    <span className="home-activity-link">
                      Explore
                      <span>→</span>
                    </span>

                  </div>

                </Link>
              ))}

            </div>
          )}

        </div>

      </div>

    </section>
  );
};

export default HomeActivities;